import { parseEmails } from '@ergo/shared';
import { type FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, errorMessage } from '../api';
import { Switch } from '../components/Switch';
import { useResource } from '../hooks';

export function PatientForm() {
  const params = useParams();
  const navigate = useNavigate();
  const patientId = params.id ? Number(params.id) : null;

  const patient = useResource(
    async () => (patientId === null ? null : api.patients.get(patientId)),
    [patientId],
  );

  const [name, setName] = useState('');
  const [contact, setContact] = useState('');
  const [email, setEmail] = useState('');
  const [emailConsent, setEmailConsent] = useState(false);
  const [notes, setNotes] = useState('');
  const [cabinet, setCabinet] = useState(false);
  const [billingLocationId, setBillingLocationId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const recipients = parseEmails(email).length;

  useEffect(() => {
    if (!patient.data) return;
    setName(patient.data.name);
    setContact(patient.data.contact ?? '');
    setEmail(patient.data.email ?? '');
    setEmailConsent(patient.data.emailConsent);
    setNotes(patient.data.notes ?? '');
    setCabinet(patient.data.cabinet);
    setBillingLocationId(patient.data.billingLocationId);
  }, [patient.data]);

  // Une visio n'a pas d'adresse : elle n'a rien à imprimer sur une facture. Les
  // lieux n'existent qu'une fois le patient créé, d'où le champ absent à la création.
  const billableLocations = (patient.data?.locations ?? []).filter(
    (location) => location.kind === 'address',
  );

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    setError(null);
    const body = {
      name: name.trim(),
      contact: contact.trim() || null,
      email: email.trim() || null,
      emailConsent,
      notes: notes.trim() || null,
      cabinet,
      billingLocationId,
    };

    try {
      if (patientId === null) {
        const created = await api.patients.create(body);
        navigate(`/patients/${created.id}`, { replace: true });
      } else {
        await api.patients.update(patientId, body);
        navigate(`/patients/${patientId}`, { replace: true });
      }
    } catch (cause) {
      setError(errorMessage(cause));
      setPending(false);
    }
  }

  return (
    <section className="page page--narrow">
      <header className="page__header">
        <h1 className="page__title">{patientId === null ? 'Nouveau patient' : 'Modifier le patient'}</h1>
      </header>

      {patient.error && <p className="error" role="alert">{patient.error}</p>}

      <form className="form" onSubmit={onSubmit}>
        <label className="field">
          <span className="field__label">Nom</span>
          <input
            className="input"
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
            autoFocus={patientId === null}
          />
        </label>

        <label className="field">
          <span className="field__label">Contact</span>
          <input
            className="input"
            placeholder="Téléphone, e-mail, parent référent…"
            value={contact}
            onChange={(event) => setContact(event.target.value)}
          />
        </label>

        <label className="field">
          <span className="field__label">Email</span>
          {/* `type="email"` refuserait une liste séparée par des points-virgules. */}
          <input
            className="input"
            type="text"
            inputMode="email"
            autoComplete="off"
            placeholder="parent1@exemple.fr, parent2@exemple.fr"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <span className="hint">
            Plusieurs adresses possibles, séparées par une virgule : chacune reçoit sa propre
            copie.
          </span>
          {recipients >= 2 && <span className="hint">{recipients} destinataires</span>}
        </label>

        <label className="checkbox">
          <input
            type="checkbox"
            checked={emailConsent}
            onChange={(event) => setEmailConsent(event.target.checked)}
          />
          <span>Accepte l’envoi des factures par email</span>
        </label>

        {billableLocations.length > 0 && (
          <label className="field">
            <span className="field__label">Adresse de facturation</span>
            <select
              className="input"
              value={billingLocationId ?? ''}
              onChange={(event) =>
                setBillingLocationId(event.target.value === '' ? null : Number(event.target.value))
              }
            >
              <option value="">— aucune —</option>
              {billableLocations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.label} — {location.formattedAddress ?? location.address}
                </option>
              ))}
            </select>
            <span className="hint">Imprimée sur les factures, sous le nom du patient.</span>
          </label>
        )}

        <Switch
          label="Patient du cabinet"
          hint="Adressé·e par le cabinet : ses règlements comptent dans la rétrocession."
          value={cabinet}
          onChange={setCabinet}
        />

        <label className="field">
          <span className="field__label">Notes</span>
          <textarea
            className="input input--textarea"
            rows={5}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </label>

        {error && <p className="error" role="alert">{error}</p>}

        <div className="form__actions">
          <button className="button button--primary" type="submit" disabled={pending || !name.trim()}>
            {pending ? 'Enregistrement…' : 'Enregistrer'}
          </button>
          <Link className="button button--ghost" to={patientId === null ? '/patients' : `/patients/${patientId}`}>
            Annuler
          </Link>
        </div>
      </form>

      {patientId === null && (
        <p className="hint">
          Après l’enregistrement, ajoutez au moins un lieu de visite : une séance se planifie
          toujours sur un lieu du patient.
        </p>
      )}
    </section>
  );
}
